import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertBanner,
  AlertBannerProvider,
  Box,
  Button,
  Icon,
} from '@saviynt/design-system';

import testIds from '../../../../constants/testIds';
import useLocalization from '../../../../hooks/use-localize';
import Logger from '../../../../services/loggerService';
import { type AttributeMappingDetails } from '../../../ManageCMDBDiscoveredApps/data-layer/discoveryConfigByIdTypes';
import { useSaveDiscoveryConfigMutation } from '../../../ManageCMDBDiscoveredApps/data-store/saveDiscoveryConfigSlice';
import { type AttributeMapping } from '../../constants/attributeConstants';
import AttributeRow from '../AttributeRow/AttributeRow';

import './AttributeMappings.css';

interface AttributeMappingsProps {
  details: AttributeMappingDetails;
  status: string;
  sortOrder: number;
  instanceKey: number;
  onDirtyChange: (isDirty: boolean) => void;
  resetVersion: number;
}

function AttributeMappings({
  details,
  status,
  sortOrder,
  instanceKey,
  onDirtyChange,
  resetVersion,
}: AttributeMappingsProps): JSX.Element {
  const formatMessage = useLocalization();

  // Local inline banner state
  const [localBanner, setLocalBanner] = useState<{
    kind: 'Success' | 'Critical';
    title: string;
    description: string;
    visible: boolean;
  }>({ kind: 'Success', title: '', description: '', visible: false });
  const closeLocalBanner = () =>
    setLocalBanner((b) => ({ ...b, visible: false }));

  const [mappings, setMappingsState] = useState<AttributeMapping[]>([]);
  const [editingServiceNowId, setEditingServiceNowId] = useState<string | null>(
    null
  );
  const [editingSaviyntId, setEditingSaviyntId] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [initialMappings, setInitialMappings] = useState<AttributeMapping[]>(
    []
  );

  const isInitializing = useRef(true);

  // Check if mappings have changed from initial state
  const checkForChanges = (newMappings: AttributeMapping[]) => {
    const hasChanges =
      JSON.stringify(newMappings) !== JSON.stringify(initialMappings);

    setIsDirty(hasChanges);

    if (!isInitializing.current) {
      onDirtyChange(hasChanges);
    }
  };

  // Wrap mapping updates so we only mark dirty _after_ initial seed completes
  const updateMappings: React.Dispatch<
    React.SetStateAction<AttributeMapping[]>
  > = (action) => {
    setMappingsState((prev) => {
      const next = typeof action === 'function' ? action(prev) : action;

      checkForChanges(next);

      return next;
    });
  };

  // ─── 1) Seed from details + reset on resetVersion ─────────────────────────────
  useEffect(() => {
    const rows = Object.entries(details.attributeMapping)
      .filter(([, serviceNowField]) => serviceNowField.trim() !== '')
      .map(([savKey, svcField]) => ({
        id: savKey,
        saviyntAttributeName: savKey,
        serviceNowAttributeName: svcField,
        saviyntAttributeValue: '',
        serviceNowAttributeValue: '',
      }));

    setMappingsState(rows);
    setInitialMappings(rows);
    setIsDirty(false);
    onDirtyChange(false);
    setEditingServiceNowId(null);
    setEditingSaviyntId(null);
    isInitializing.current = true;
  }, [
    details,
    resetVersion,
    onDirtyChange,
    setEditingServiceNowId,
    setEditingSaviyntId,
  ]);

  // ─── 2) Once that initial seed has set `mappings`, turn off init mode ───────────
  useEffect(() => {
    if (isInitializing.current) {
      isInitializing.current = false;
    }
  }, [mappings]);

  const serviceNowOptions = details.service_now_fields;
  const saviyntOptions = Object.keys(details.attributeMapping);
  const isDisabled = status === 'Disabled';

  // Use useMemo to ensure this calculation updates when mappings change
  const canAddMoreRows = useMemo(() => {
    // Calculate which options are currently in use (excluding empty/undefined values)
    const usedServiceNowFields = new Set(
      mappings
        .map((m) => m.serviceNowAttributeName)
        .filter((name) => name && name.trim() !== '')
    );
    const usedSaviyntFields = new Set(
      mappings
        .map((m) => m.saviyntAttributeName)
        .filter((name) => name && name.trim() !== '')
    );

    // Calculate available options (not currently used)
    const availableServiceNowOptions = serviceNowOptions.filter(
      (option) => !usedServiceNowFields.has(option)
    );
    const availableSaviyntOptions = saviyntOptions.filter(
      (option) => !usedSaviyntFields.has(option)
    );

    // Can add more rows if there are available options in both lists
    return (
      availableServiceNowOptions.length > 0 &&
      availableSaviyntOptions.length > 0
    );
  }, [mappings, serviceNowOptions, saviyntOptions]);

  const canSave =
    !isDisabled &&
    mappings.length > 0 &&
    mappings.every(
      (r) => r.serviceNowAttributeName.trim() && r.saviyntAttributeName.trim()
    ) &&
    isDirty;

  const [saveDiscoveryConfig, { isLoading: isSaving }] =
    useSaveDiscoveryConfigMutation();

  // ─── Add ────────────────────────────────────────────────────────────────────────
  const handleAddAttribute = (): void => {
    if (!canAddMoreRows) {
      return;
    }

    const newRow: AttributeMapping = {
      id: `row-${Date.now()}`,
      saviyntAttributeName: '',
      serviceNowAttributeName: '',
      saviyntAttributeValue: '',
      serviceNowAttributeValue: '',
    };

    updateMappings((prev) => [...prev, newRow]);
    setEditingServiceNowId(newRow.id);
  };

  // ─── Delete ─────────────────────────────────────────────────────────────────────
  const handleDeleteRow = (idToDelete: string): void => {
    updateMappings((prev) => prev.filter((r) => r.id !== idToDelete));
  };

  // ─── Save ───────────────────────────────────────────────────────────────────────
  const handleSave = async (): Promise<void> => {
    const initialKeys = Object.keys(details.attributeMapping);
    const currentMap = mappings.reduce<Record<string, string>>((acc, r) => {
      if (r.saviyntAttributeName) {
        acc[r.saviyntAttributeName] = r.serviceNowAttributeName;
      }

      return acc;
    }, {});

    const fullMapping: Record<string, string> = {};

    initialKeys.forEach((key) => {
      fullMapping[key] = currentMap[key] ?? '';
    });

    const payload = {
      id: instanceKey,
      tabs: [
        {
          sortOrder,
          name: 'Attribute_Mapping',
          details: { attributeMapping: fullMapping },
        },
      ],
    };

    try {
      const response = await saveDiscoveryConfig(payload).unwrap();

      Logger.log('Attribute mapping saved successfully');
      setInitialMappings(mappings);
      setIsDirty(false);
      onDirtyChange(false);
      setLocalBanner({
        kind: 'Success',
        title: formatMessage('INTEGRATIONS.BUTTON.SAVED', 'Saved'),
        description: response.message || 'Attribute mapping saved successfully',
        visible: true,
      });
    } catch (err: unknown) {
      Logger.error('Failed to save attribute mapping:', err);
      const errorData = err as { data?: { message?: string } };
      const message =
        errorData.data?.message || 'Failed to save attribute mapping';

      setLocalBanner({
        kind: 'Critical',
        title: formatMessage('INTEGRATIONS.BUTTON.SAVE_ERROR_TITLE'),
        description: message,
        visible: true,
      });
    }
  };

  return (
    <div
      className='attribute-mappings'
      data-testid={testIds.ATTRIBUTE_MAPPINGS_CONTAINER}>
      {/* Inline child banner */}
      <AlertBannerProvider>
        <AlertBanner
          colorTheme={localBanner.kind}
          title={localBanner.title}
          description={localBanner.description}
          isVisible={localBanner.visible}
          onCancel={closeLocalBanner}
          className='attribute-mappings-alertBanner'
          dataTestId={testIds.ATTRIBUTE_MAPPINGS_ALERT_BANNER}
        />
      </AlertBannerProvider>

      <Box
        className='attribute-mappings__wrapper'
        data-testid={testIds.ATTRIBUTE_MAPPINGS_WRAPPER}>
        <div
          className='attribute-mappings__grid'
          data-testid={testIds.ATTRIBUTE_MAPPINGS_GRID}>
          <div className='attribute-mappings__header-row'>
            <AttributeRow.Header />
          </div>

          {mappings.map((row) => (
            <AttributeRow
              key={row.id}
              row={row}
              mappings={mappings}
              setMappings={updateMappings}
              editingServiceNowId={editingServiceNowId}
              editingSaviyntId={editingSaviyntId}
              setEditingServiceNowId={setEditingServiceNowId}
              setEditingSaviyntId={setEditingSaviyntId}
              serviceNowOptions={serviceNowOptions}
              saviyntOptions={saviyntOptions}
              onDelete={() => handleDeleteRow(row.id)}
              isDisabled={isDisabled}
            />
          ))}
        </div>
      </Box>

      <div className='attribute-mappings__actions'>
        <Button
          type='button'
          kind='subtle'
          size='small'
          leftIcon={<Icon kind='Add' />}
          onClick={handleAddAttribute}
          isDisabled={isDisabled || !canAddMoreRows}
          aria-label={formatMessage(
            'INTEGRATIONS.ATTRIBUTE_MAPPINGS.BUTTON.ADD_ROW_ARIA_LABEL'
          )}
          dataTestId={testIds.ATTRIBUTE_MAPPINGS_ADD_BUTTON}>
          {formatMessage(
            'INTEGRATIONS.ATTRIBUTE_MAPPINGS.BUTTON.ADD_ATTRIBUTE'
          )}
        </Button>
      </div>

      <div
        className='attribute-mappings__footer'
        data-testid={testIds.ATTRIBUTE_MAPPINGS_FOOTER}>
        <Button
          type='button'
          kind='filled'
          onClick={handleSave}
          isDisabled={!canSave || isSaving}
          isLoading={isSaving}
          loaderText={formatMessage('INTEGRATIONS.BUTTON.SAVING')}
          dataTestId={testIds.ATTRIBUTE_MAPPINGS_SAVE_BUTTON}>
          {formatMessage('INTEGRATIONS.BUTTON.SAVE')}
        </Button>
      </div>
    </div>
  );
}

export default React.memo(AttributeMappings);
